"use client";

import { hasAnyRoles, RoleType } from "@entities/user";
import { AcademicCapIcon, CogIcon } from "@heroicons/react/24/solid";
import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { CommonComponent, UserPopupNavigationItem } from "@shared/cms";
import { getNacoAvatar } from "@shared/naco";
import { getInitial } from "@shared/utils";
import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ubud-ui/react-ui-kit";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import React from "react";

function DashboardDropdownContent() {
  const clientConfig = useClientConfig();
  const user = clientConfig?.user;

  const roleNavs: {
    title: string;
    icon: React.ReactNode;
    description: string;
    show: boolean;
    buttonLabel: string;
    link: string;
  }[] = [
    {
      title: "Dasbor Admin Karirhub",
      description: "Akses untuk mengatur dan mengelola lowongan pada semua perusahaan yang terdaftar.",
      icon: <CogIcon className="size-5 text-primary" />,
      show: hasAnyRoles(user, [RoleType.ADMIN, RoleType.ADMIN_CITY, RoleType.ADMIN_PROVINCE]),
      buttonLabel: "Akses Dasbor",
      link: clientConfig?.config.consoleAppUrl || "https://localhost:4200",
    },
    {
      title: "Daftar sebagai Pemberi Kerja",
      description: "Pengelolaan lowongan, dan manajemen tenaga kerja pada perusahaan Anda.",
      icon: <AcademicCapIcon className="size-5 text-primary" />,
      show: !hasAnyRoles(user, [RoleType.EMPLOYER]),
      buttonLabel: "Daftar",
      link: clientConfig?.config.employerAppUrl
        ? clientConfig.config.employerAppUrl + "/auth/register/employers"
        : "https://localhost:4200",
    },
    {
      title: "Dasbor Pemberi Kerja",
      description: "Pengelolaan lowongan, dan manajemen tenaga kerja pada perusahaan Anda.",
      icon: <AcademicCapIcon className="size-5 text-primary" />,
      show: hasAnyRoles(user, [RoleType.EMPLOYER]),
      buttonLabel: "Akses Dasbor",
      link: clientConfig?.config.employerAppUrl ? clientConfig.config.employerAppUrl : "https://localhost:4200",
    },
  ];

  if (!user || roleNavs.filter((it) => !!it.show)?.length <= 0) {
    return <></>;
  }

  return (
    <div className="space-y-6">
      {roleNavs
        .filter((it) => !!it.show)
        .map((nav) => {
          return (
            <div className="bg-white border border-gray-200 p-4 rounded-xl" key={nav.title}>
              <div className="flex items-center space-x-2 text-base font-bold">
                {nav.icon}
                <span className="flex-1">{nav.title}</span>
              </div>
              <div className="text-gray-500 text-sm mt-3">{nav.description}</div>
              <div className="mt-4">
                <Button asChild className="font-bold text-sm">
                  <Link href={nav.link}>{nav.buttonLabel}</Link>
                </Button>
              </div>
            </div>
          );
        })}
    </div>
  );
}

function DashboardLink() {
  const clientConfig = useClientConfig();
  const config = clientConfig?.layoutSetting;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="link"
          className="text-sm whitespace-nowrap p-0 m-0 w-auto h-auto text-gray-900 hover:no-underline hover:opacity-80"
        >
          {config?.header?.dashboardButtonText}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={34}
        className={cn("w-[100vw] max-h-[80vh] rounded-xl overflow-x-hidden overflow-y-auto md:w-[40vw] max-w-[450px]", "p-8")}
      >
        <DashboardDropdownContent />
      </PopoverContent>
    </Popover>
  );
}

function DashboardButton() {
  const clientConfig = useClientConfig();
  const config = clientConfig?.layoutSetting;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="font-bold text-sm w-full mt-4" variant="outline">
          {config?.header?.dashboardButtonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[18rem]">
        <DialogTitle>{config?.header?.dashboardButtonText}</DialogTitle>
        <DialogBody>
          <div className="mt-4">
            <DashboardDropdownContent />
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function HeaderUserPopup() {
  const clientConfig = useClientConfig();
  const config = clientConfig?.layoutSetting;
  const user = clientConfig?.nacoUser;

  const pathname = usePathname();

  const getParsedHref = (target: string) => {
    if (target === "/api/naco/logout") {
      return `/api/naco/logout?redirect_url=${pathname}`;
    }

    return target;
  };

  const getNavLink = (nav: UserPopupNavigationItem, i: number) => {
    if (nav.__component === CommonComponent.LinkList) {
      return (
        <div key={nav.id}>
          {nav.list.map((it) => {
            return (
              <DropdownMenuItem key={it.id} asChild className="text-base cursor-pointer font-medium">
                <Link href={getParsedHref(it.href)} prefetch={false} target={it.target}>
                  {it.icon && (
                    <Image
                      src={it.icon?.url}
                      alt={it.icon?.alternativeText || it.icon?.name || ""}
                      className="size-6 min-w-6"
                      width={it.icon?.width || 24}
                      height={it.icon?.height || 24}
                    />
                  )}
                  {it.text}
                </Link>
              </DropdownMenuItem>
            );
          })}
          {i !== (config?.userPopupNavigation?.length || 0) - 1 && <DropdownMenuSeparator />}
        </div>
      );
    }

    return (
      <DropdownMenuItem key={nav.id} asChild className="text-base cursor-pointer font-medium">
        <Link href={getParsedHref(nav.href)} prefetch={false} target={nav.target}>
          {nav.icon && (
            <Image
              src={nav.icon?.url}
              alt={nav.icon?.alternativeText || nav.icon?.name || ""}
              className="size-6 min-w-6"
              width={nav.icon?.width || 24}
              height={nav.icon?.height || 24}
            />
          )}
          {nav.text}
        </Link>
      </DropdownMenuItem>
    );
  };

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        {config?.header?.registerButtonText && (
          <Button variant="outline" className="font-bold">
            <Link href="/pendaftaran" prefetch={false}>
              {config?.header?.registerButtonText}
            </Link>
          </Button>
        )}
        <Button variant="default" className="font-bold" asChild>
          <Link href={"/api/naco/login?redirect_url=" + pathname} prefetch={false}>
            {config?.header?.loginButtonText || "Masuk"}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-6">
      <div className="hidden lg:inline-block">
        <DashboardLink />
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full">
            <span className="sr-only">Open user menu</span>
            <div className="h-11 w-11 min-w-11 rounded-full bg-primary-foreground text-primary text-center items-center justify-center inline-flex text-base font-medium overflow-hidden">
              <Image
                src={getNacoAvatar(user)}
                alt={getInitial(user?.name || "")}
                width={44}
                height={44}
                className="w-full h-full object-cover"
              />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[18rem]">
          <DropdownMenuLabel className="p-3 font-normal">
            <div className="w-16 h-16 inline-flex items-center justify-center text-center bg-primary-foreground rounded-full font-medium overflow-hidden">
              <Image
                src={getNacoAvatar(user)}
                alt={getInitial(user?.name || "")}
                width={56}
                height={56}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-4">
              <h4 className="text-lg m-0 p-0 font-bold">{user?.name}</h4>
              <p className="p-0 m-0 text-base text-gray-500">{user?.email}</p>
            </div>
            <div className="lg:hidden">
              <DashboardButton />
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {config?.userPopupNavigation?.map((nav) => getNavLink(nav, nav.id))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
